import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  List as ListIcon,
} from '@mui/icons-material';
import { Autocomplete, TextField, Box, Typography, Chip, CircularProgress, IconButton, Button } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchAddressesAPI, addressToFakeContact } from '@sophocles/api/addresses';
import { formatPhoneDisplay } from '@sophocles/utils/phoneUtils';

import AddressForm from '../AddressForm/AddressForm';
import { useContactFetch } from '../hooks/useContactFetch';
import PrimaryContactConfirmDialog from '../PrimaryContactConfirmDialog';

/**
 * ContactPicker - Reusable address book dropdown with manual entry mode
 *
 * Enhanced with Address model integration and manual entry toggle
 *
 * @param {Object} props
 * @param {number} props.customerId - Customer ID
 * @param {number} props.siteGroupId - Site Group ID (optional)
 * @param {function} props.onChange - Callback when contact selected: (contact) => void
 * @param {Object} props.value - Currently selected contact
 * @param {boolean} props.completeAddressOnly - Filter to complete addresses only (default: true)
 * @param {string} props.label - Input label (default: "Select Contact")
 * @param {boolean} props.enablePrimaryActions - Show "Mark as Primary" action button (default: true for RMA, false for Orders)
 * @param {function} props.onPrimaryChange - Callback when primary status changes (contactId, isPrimary)
 * @param {string} props.addressType - 'billing' or 'shipping' (for sorting and filtering Address records)
 * @param {boolean} props.showDefaultAddresses - Show Address model records (default: true, false for support contact)
 */
const ContactPicker = ({
  customerId,
  siteGroupId,
  onChange,
  value,
  completeAddressOnly = true,
  label,
  placeholder,
  disabled = false,
  enablePrimaryActions = true,
  onPrimaryChange,
  addressType = 'shipping',
  showDefaultAddresses = true,
}) => {
  const { t } = useTranslation();
  const { contacts, loading, error, refetch } = useContactFetch({
    customerId,
    siteGroupId,
    completeAddressOnly,
  });

  const [inputValue, setInputValue] = useState('');
  const [primaryDialogOpen, setPrimaryDialogOpen] = useState(false);
  const [selectedContactForPrimary, setSelectedContactForPrimary] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [addresses, setAddresses] = useState([]);

  // Check if current value is manual entry (no contact ID and not an address)
  useEffect(() => {
    if (value && !value.id && !value._isAddress) {
      setManualMode(true);
    }
  }, [value]);

  const fetchAddressesForPicker = async () => {
    try {
      const filters = {
        primary_only: true,
        is_billing: addressType === 'billing',
      };
      if (customerId) filters.customer = customerId;
      if (siteGroupId) filters.site_group = siteGroupId;

      const addressRecords = await fetchAddressesAPI(filters);

      // Convert to fake contacts with business name
      const fakeContacts = addressRecords.map(addr => {
        // Determine business name based on scope
        const businessName = addr.customer ? t('contacts.customerDefault') : t('contacts.siteGroupDefault');
        return addressToFakeContact(addr, businessName);
      });

      setAddresses(fakeContacts);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      setAddresses([]);
    }
  };

  // Fetch addresses if showDefaultAddresses is true
  useEffect(() => {
    if (showDefaultAddresses && (customerId || siteGroupId)) {
      fetchAddressesForPicker();
    }
  }, [customerId, siteGroupId, addressType, showDefaultAddresses]);

  const toggleManualMode = () => {
    setManualMode(!manualMode);
    if (!manualMode) {
      // Entering manual mode - clear selection
      onChange(null);
    }
  };

  // Get sorted options: Addresses first (by type), then primary contacts, then others
  const getSortedOptions = () => {
    const allOptions = [...addresses, ...contacts];

    const sorted = allOptions.sort((a, b) => {
      // Priority scoring - prioritize RMA primaries for RMA context
      const getPriority = item => {
        if (item._isAddress) {
          if (addressType === 'billing' && item.is_primary_rma_billing) return 1;
          if (addressType === 'shipping' && item.is_primary_rma_shipping) return 2;
          return 5; // Other addresses
        } else {
          if (item.is_primary_rma_billing) return 3;
          if (item.is_primary_rma_shipping) return 4;
          return 6; // Other contacts
        }
      };

      return getPriority(a) - getPriority(b);
    });

    return sorted;
  };

  // Handle primary icon click
  const handlePrimaryClick = (e, contact) => {
    e.stopPropagation(); // Don't trigger contact selection
    setSelectedContactForPrimary(contact);
    setPrimaryDialogOpen(true);
  };

  // Handle primary confirmation
  const handlePrimaryConfirm = async () => {
    if (selectedContactForPrimary && onPrimaryChange) {
      const isPrimary = !selectedContactForPrimary.is_primary_rma;
      await onPrimaryChange(selectedContactForPrimary.id, isPrimary);
    }
    setPrimaryDialogOpen(false);
    setSelectedContactForPrimary(null);
  };

  // Handle primary dialog cancel
  const handlePrimaryCancel = () => {
    setPrimaryDialogOpen(false);
    setSelectedContactForPrimary(null);
  };

  // Use translation defaults if not provided
  const displayLabel = label || t('contacts.selectContact');
  const displayPlaceholder = placeholder || t('contacts.searchContacts');

  return (
    <Box>
      {manualMode ? (
        // Manual Entry Mode
        <Box>
          <Button
            variant="text"
            startIcon={<ListIcon />}
            onClick={toggleManualMode}
            size="small"
            sx={{ mb: 1 }}
            disabled={disabled}
          >
            {t('contacts.selectFromSavedAddresses')}
          </Button>
          <AddressForm
            prefix=""
            values={value || {}}
            onChange={e => {
              const field = e.target.name;
              const newValue = e.target.value;
              onChange({
                ...value,
                [field]: newValue,
              });
            }}
            onBlur={() => {}}
            disabled={disabled}
            showBusinessName={true}
          />
        </Box>
      ) : (
        // Selection Mode
        <Box>
          <Button
            variant="text"
            startIcon={<EditIcon />}
            onClick={toggleManualMode}
            size="small"
            sx={{ mb: 1 }}
            disabled={disabled}
          >
            {t('contacts.enterDetailsManually')}
          </Button>
          <Autocomplete
            options={getSortedOptions()}
            loading={loading}
            disabled={disabled}
            value={value}
            onChange={(event, newValue) => onChange(newValue)}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            getOptionLabel={option => {
              if (option._isAddress) {
                return option.business_name || '';
              }
              return option.name || '';
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={params => (
              <TextField
                {...params}
                label={displayLabel}
                placeholder={displayPlaceholder}
                error={!!error}
                helperText={error ? t('contacts.failedToLoadContacts') : ''}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => {
              const isAddress = option._isAddress;

              return (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'flex-start', py: 1.5, gap: 1 }}>
                  {/* Icon for Address vs Contact */}
                  {isAddress ? (
                    <BusinessIcon sx={{ mt: 0.5, color: 'primary.main' }} />
                  ) : (
                    <PersonIcon sx={{ mt: 0.5, color: 'text.secondary' }} />
                  )}

                  {/* Star Icon Action (if enabled and not an address) */}
                  {!isAddress && enablePrimaryActions && (
                    <IconButton
                      size="small"
                      onClick={e => handlePrimaryClick(e, option)}
                      sx={{ mt: 0.5 }}
                      title={
                        addressType === 'billing'
                          ? option.is_primary_rma_billing
                            ? t('contacts.unmarkPrimary')
                            : t('contacts.markPrimary')
                          : option.is_primary_rma_shipping
                            ? t('contacts.unmarkPrimary')
                            : t('contacts.markPrimary')
                      }
                    >
                      {(addressType === 'billing' ? option.is_primary_rma_billing : option.is_primary_rma_shipping) ? (
                        <StarIcon color="primary" fontSize="small" />
                      ) : (
                        <StarBorderIcon color="action" fontSize="small" />
                      )}
                    </IconButton>
                  )}

                  {/* Contact/Address Information */}
                  <Box sx={{ flex: 1 }}>
                    {/* Name/Business Name and Primary Badges */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                      {option.is_primary_rma_billing && (
                        <Chip
                          icon={<StarIcon />}
                          label={t('contacts.primary.rmaBilling', 'RMA Billing')}
                          size="small"
                          color="primary"
                          sx={{ height: 20 }}
                        />
                      )}
                      {option.is_primary_rma_shipping && (
                        <Chip
                          icon={<StarIcon />}
                          label={t('contacts.primary.rmaShipping', 'RMA Shipping')}
                          size="small"
                          color="secondary"
                          sx={{ height: 20 }}
                        />
                      )}
                      <Typography variant="body1" sx={{ fontWeight: isAddress ? 600 : 500 }}>
                        {isAddress ? (
                          // Address: Show business name only
                          <strong>{option.business_name}</strong>
                        ) : (
                          // Contact: Show person name + business name
                          <>
                            {option.name}
                            {option.business_name && (
                              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                - {option.business_name}
                              </Typography>
                            )}
                          </>
                        )}
                      </Typography>
                    </Box>

                    {/* Email and Phone (only for Contact records) */}
                    {!isAddress && (
                      <Typography variant="body2" color="text.secondary">
                        {option.email} {option.phone1 && `| ${formatPhoneDisplay(option.phone1)}`}
                      </Typography>
                    )}

                    {/* Address Preview */}
                    {(option.full_address_display || option.address) && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {option.full_address_display ||
                          [option.address, option.city, option.state, option.zipcode].filter(Boolean).join(', ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            noOptionsText={
              loading
                ? t('contacts.loadingContacts')
                : inputValue
                  ? t('contacts.noContactsFound')
                  : t('contacts.startTypingToSearch')
            }
          />
        </Box>
      )}

      {/* Primary Contact Confirmation Dialog */}
      {enablePrimaryActions && (
        <PrimaryContactConfirmDialog
          open={primaryDialogOpen}
          onClose={handlePrimaryCancel}
          contact={selectedContactForPrimary}
          onConfirm={handlePrimaryConfirm}
          onCancel={handlePrimaryCancel}
          primaryType={addressType}
        />
      )}
    </Box>
  );
};

ContactPicker.propTypes = {
  customerId: PropTypes.number,
  siteGroupId: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.object,
  completeAddressOnly: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  enablePrimaryActions: PropTypes.bool,
  onPrimaryChange: PropTypes.func,
  addressType: PropTypes.oneOf(['billing', 'shipping']),
  showDefaultAddresses: PropTypes.bool,
};

export default ContactPicker;
